import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, TrendingUp, Shield, Building2, Users, DollarSign,
  CheckCircle2, ChevronRight, BarChart3, Briefcase, Target,
  FileText, Lock, MapPin, Clock, Handshake, Award,
  Landmark, Scale, Eye, LineChart, Layers, Heart
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';


const FamilyOfficesPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Capital preservation benefits
  const preservationBenefits = [
    {
      icon: Shield,
      title: 'Defensive Asset Selection',
      description: 'Focus on workforce housing with stable demand across economic cycles'
    },
    {
      icon: TrendingUp,
      title: 'Predictable Cash Flow',
      description: 'Income-generating properties with quarterly distribution potential'
    },
    {
      icon: Target,
      title: 'Conservative Underwriting',
      description: 'Built-in margin of safety with stress-tested assumptions'
    },
    {
      icon: Layers,
      title: 'Diversified Exposure',
      description: 'Multiple properties and submarkets to reduce concentration risk'
    }
  ];

  // Alignment features
  const alignmentFeatures = [
    {
      icon: Handshake,
      title: 'GP Co-Investment',
      description: 'Sponsor capital invested alongside LPs in every deal'
    },
    {
      icon: Scale,
      title: 'Aligned Fee Structure',
      description: 'Performance-based incentives tied to investor outcomes'
    },
    {
      icon: Clock,
      title: 'Long-Term Partnerships',
      description: 'Multi-fund relationships built on trust and performance'
    },
    {
      icon: Heart,
      title: 'Stewardship Mindset',
      description: 'Capital preservation as foundational investment principle'
    }
  ];

  // Governance features
  const governanceFeatures = [
    { icon: FileText, title: 'Quarterly Reports', description: 'Detailed property-level performance updates' },
    { icon: Lock, title: 'Investor Portal', description: 'Secure 24/7 access to documents and data' },
    { icon: Users, title: 'Direct Access', description: 'Open communication with investment team' },
    { icon: Eye, title: 'Full Transparency', description: 'Clear visibility into fees, holdings, and operations' }
  ];

  // Flexible structure options
  const flexibleOptions = [
    {
      title: 'Standard Fund LP',
      minimum: '$250,000',
      description: 'Commingled fund investment with diversified exposure',
      features: ['Quarterly distributions', 'Full reporting suite', 'Voting rights']
    },
    {
      title: 'Co-Investment',
      minimum: '$500,000+',
      description: 'Direct participation in select deals alongside the fund',
      features: ['Deal-by-deal selection', 'Enhanced economics', 'Greater control']
    },
    {
      title: 'Custom Allocation',
      minimum: '$1,000,000+',
      description: 'Tailored investment structure for larger commitments',
      features: ['Negotiated terms', 'Dedicated reporting', 'Strategic partnership']
    }
  ];

  // Houston market highlights
  const marketHighlights = [
    { stat: '7M+', label: 'Metro Population', description: 'Fourth largest US metro' },
    { stat: '#1', label: 'Job Growth', description: 'Leading employment expansion' },
    { stat: '15+', label: 'Years Local', description: 'Deep market expertise' },
    { stat: '70+', label: 'Deals Closed', description: 'Proven execution track record' }
  ];

  return (
    <>
      <Helmet>
        <title>Real Estate Investments for Family Offices | ASAP Capital</title>
        <meta 
          name="description" 
          content="Long-term, value-add real estate investment opportunities for family offices focused on capital preservation and multigenerational wealth." 
        />
        <meta name="keywords" content="real estate investments for family offices, private equity real estate funds, multigenerational wealth investing, family office real estate, Houston real estate investments" />
        <link rel="canonical" href="/investors/family-offices" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#D4AF37]/5 rounded-full blur-[180px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D4AF37]/3 rounded-full blur-[150px] pointer-events-none" />
            
            {/* Subtle pattern */}
            <div className="absolute inset-0 opacity-[0.02]">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, #D4AF37 1px, transparent 0)`,
                backgroundSize: '40px 40px'
              }} />
            </div>
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">For Family Offices</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                  variants={itemVariants}
                >
                  Real Estate Investment Opportunities for{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Family Offices
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/60 leading-relaxed mb-8 max-w-2xl"
                  variants={itemVariants}
                >
                  Partner with a disciplined sponsor focused on capital preservation, transparent 
                  governance, and long-term value creation across Texas real estate markets.
                </motion.p>

                {/* Key differentiators */}
                <motion.div 
                  className="flex flex-wrap gap-6 mb-10"
                  variants={itemVariants}
                >
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">$250K Minimum</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Handshake className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">GP Co-Investment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">Long-Term Focus</span>
                  </div>
                </motion.div>

                {/* CTA buttons */}
                <motion.div 
                  className="flex flex-wrap gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Schedule Consultation
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      View Fund Terms
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: CAPITAL PRESERVATION & INCOME ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Investment Philosophy
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Capital Preservation & Income
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Our investment approach prioritizes protecting principal while generating 
                      stable, predictable income. We target defensive asset classes with proven 
                      demand across economic cycles—workforce housing that families need regardless 
                      of market conditions.
                    </motion.p>
                    
                    <motion.div 
                      className="grid sm:grid-cols-2 gap-4"
                      variants={itemVariants}
                    >
                      {preservationBenefits.map((benefit, index) => (
                        <motion.div
                          key={benefit.title}
                          className="bg-[#111D35]/50 rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/20 transition-all group"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -3 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4AF37]/20 transition-colors">
                            <benefit.icon className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <h4 className="text-white font-semibold text-sm mb-1">{benefit.title}</h4>
                          <p className="text-white/40 text-xs">{benefit.description}</p>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Visual card */}
                  <motion.div 
                    variants={fadeInUp}
                    className="relative"
                  >
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-8">
                          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                            <Shield className="h-7 w-7 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">Multigenerational Focus</h4>
                            <p className="text-white/40 text-sm">Wealth preservation first</p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Zero Investor Losses</span>
                            <span className="text-emerald-400 font-semibold">18+ Years</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Capital Returned</span>
                            <span className="text-white font-semibold">100%</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Preferred Return</span>
                            <span className="text-[#D4AF37] font-semibold">8%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: VALUE-ADD STRATEGY ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Visual - Strategy diagram */}
                  <motion.div variants={fadeInUp} className="order-2 lg:order-1">
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <h4 className="text-white font-semibold mb-6">Value Creation Process</h4>
                        
                        <div className="space-y-4">
                          {[
                            { phase: 'Acquire', desc: 'Below-market entry through proprietary sourcing', progress: 25 },
                            { phase: 'Improve', desc: 'Strategic renovations and operational upgrades', progress: 50 },
                            { phase: 'Stabilize', desc: 'Occupancy optimization and NOI growth', progress: 75 },
                            { phase: 'Exit', desc: 'Disciplined disposition at peak value', progress: 100 }
                          ].map((step, index) => (
                            <div key={step.phase} className="relative">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-white text-sm font-medium">{step.phase}</span>
                                <span className="text-[#D4AF37] text-xs">{step.progress}%</span>
                              </div>
                              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <motion.div 
                                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] rounded-full"
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${step.progress}%` }}
                                  viewport={{ once: true }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </div>
                              <p className="text-white/40 text-xs mt-1">{step.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div className="order-1 lg:order-2">
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Disciplined Execution
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Value-Add Strategy
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      We create sustainable wealth through disciplined value-add execution—targeting 
                      properties with clear improvement potential and executing renovation programs 
                      that capture rent premiums while enhancing asset quality.
                    </motion.p>
                    
                    <motion.div className="space-y-3 mb-8" variants={itemVariants}>
                      {[
                        'Renovation-driven NOI growth',
                        'Operational efficiency improvements',
                        'Risk-adjusted return targeting',
                        'Conservative leverage policies'
                      ].map((point, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/60">{point}</span>
                        </div>
                      ))}
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Link to="/strategy">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          View Full Strategy
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: ALIGNED INTERESTS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    True Partnership
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Aligned Interests
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    We invest meaningful capital alongside our LPs in every deal, ensuring our 
                    interests are fundamentally aligned with family office partners.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {alignmentFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group text-center"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8 }}
                    >
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors border border-[#D4AF37]/20">
                        <feature.icon className="h-7 w-7 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-white/50 text-sm">{feature.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: GOVERNANCE & REPORTING ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Institutional Standards
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Governance & Reporting
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Family offices expect—and deserve—institutional-grade governance. We maintain 
                      rigorous reporting standards, open communication channels, and full transparency 
                      into our operations, holdings, and decision-making.
                    </motion.p>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {governanceFeatures.map((feature, index) => (
                        <motion.div
                          key={feature.title}
                          className="flex items-start gap-3 p-4 bg-[#111D35]/50 rounded-xl border border-white/5 hover:border-[#D4AF37]/20 transition-colors"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                            <feature.icon className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold text-sm">{feature.title}</h4>
                            <p className="text-white/40 text-xs mt-1">{feature.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Visual card */}
                  <motion.div variants={fadeInUp}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <div className="text-center mb-6">
                          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                            <Eye className="h-8 w-8 text-[#D4AF37]" />
                          </div>
                          <h4 className="text-white font-semibold text-lg">Complete Transparency</h4>
                          <p className="text-white/40 text-sm mt-1">Visibility into every aspect of operations</p>
                        </div>

                        <div className="space-y-3">
                          {[
                            'Property-level financial statements',
                            'Capital call and distribution notices',
                            'Quarterly investor letters',
                            'Annual meeting and site visits'
                          ].map((item, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-[#0A1628]/50 rounded-lg border border-white/5">
                              <CheckCircle2 className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                              <span className="text-white/60 text-sm">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: HOUSTON & TEXAS MARKETS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Stats grid */}
                  <motion.div 
                    className="grid grid-cols-2 gap-4"
                    variants={fadeInUp}
                  >
                    {marketHighlights.map((item, index) => (
                      <motion.div
                        key={item.label}
                        className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 text-center hover:border-[#D4AF37]/20 transition-colors"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <p className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent mb-2">
                          {item.stat}
                        </p>
                        <p className="text-white font-medium text-sm mb-1">{item.label}</p>
                        <p className="text-white/40 text-xs">{item.description}</p>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Market Expertise
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Houston & Texas Markets
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      Our deep local expertise provides proprietary deal sourcing and submarket 
                      knowledge that out-of-state investors cannot replicate. Houston's demographic 
                      growth and business-friendly environment create exceptional long-term opportunities.
                    </motion.p>
                    
                    <motion.div className="space-y-3 mb-8" variants={itemVariants}>
                      {[
                        'Proprietary local relationships for deal flow',
                        'Deep submarket and asset-level knowledge',
                        'On-the-ground operations and oversight',
                        'Proven execution across market cycles'
                      ].map((point, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/60">{point}</span>
                        </div>
                      ))}
                    </motion.div>

                    <motion.div className="flex flex-wrap gap-3" variants={itemVariants}>
                      <Link to="/why-houston">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          Why Houston
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                          View Portfolio
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: FLEXIBLE ALLOCATIONS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Customized Solutions
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Flexible Allocations
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    We offer multiple investment structures to accommodate varying commitment 
                    sizes and family office preferences.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {flexibleOptions.map((option, index) => (
                    <motion.div
                      key={option.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group relative overflow-hidden"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      whileHover={{ y: -5 }}
                    >
                      {index === 1 && (
                        <div className="absolute top-4 right-4 px-2 py-1 bg-[#D4AF37]/20 rounded text-[#D4AF37] text-xs font-semibold">
                          Popular
                        </div>
                      )}
                      
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {option.title}
                      </h3>
                      <p className="text-[#D4AF37] font-semibold text-lg mb-3">{option.minimum}</p>
                      <p className="text-white/50 text-sm mb-5">{option.description}</p>
                      
                      <div className="space-y-2 pt-4 border-t border-white/10">
                        {option.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                            <span className="text-white/60 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="text-center mt-10"
                  variants={itemVariants}
                >
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2 mx-auto">
                      View Complete Fund Terms
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: PROVEN TRACK RECORD ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Demonstrated Results
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Proven Track Record
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      Our performance across multiple market cycles demonstrates disciplined 
                      execution and consistent value creation. Review our realized investments 
                      and case studies to evaluate our approach.
                    </motion.p>

                    <motion.div className="flex flex-wrap gap-3" variants={itemVariants}>
                      <Link to="/track-record">
                        <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-5 py-2.5 h-11 rounded-full text-sm flex items-center gap-2">
                          View Track Record
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                          Case Studies
                        </Button>
                      </Link>
                    </motion.div>
                  </div>

                  {/* Stats */}
                  <motion.div 
                    className="grid grid-cols-2 gap-4"
                    variants={fadeInUp}
                  >
                    <div className="text-center p-5 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-3xl lg:text-4xl font-bold text-[#D4AF37]">18+</p>
                      <p className="text-white/40 text-sm mt-1">Years Operating</p>
                    </div>
                    <div className="text-center p-5 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-3xl lg:text-4xl font-bold text-white">70+</p>
                      <p className="text-white/40 text-sm mt-1">Deals Completed</p>
                    </div>
                    <div className="text-center p-5 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-3xl lg:text-4xl font-bold text-emerald-400">100%</p>
                      <p className="text-white/40 text-sm mt-1">Capital Returned</p>
                    </div>
                    <div className="text-center p-5 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-3xl lg:text-4xl font-bold text-white">28%</p>
                      <p className="text-white/40 text-sm mt-1">Average IRR</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FINAL CTA ==================== */}
          <section className="py-24 lg:py-32 relative bg-[#111D35]">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d1a2d] via-transparent to-[#0d1a2d]" />
            
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.span 
                  className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                  variants={itemVariants}
                >
                  Begin the Conversation
                </motion.span>
                <motion.h2 
                  className="text-2xl lg:text-4xl font-bold text-white mb-5"
                  variants={itemVariants}
                >
                  Partner With ASAP Capital
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-10"
                  variants={itemVariants}
                >
                  Schedule a consultation to discuss how our disciplined real estate strategy 
                  supports multigenerational wealth preservation and long-term family office objectives.
                </motion.p>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-8 py-3 h-14 rounded-full text-base">
                      Schedule Consultation
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-14 rounded-full text-base">
                      Request Investment Materials
                    </Button>
                  </Link>
                </motion.div>

                {/* Compliance note */}
                <motion.p 
                  className="text-white/30 text-xs mt-10 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  ASAP Capital investments are available exclusively to qualified investors. 
                  All investments involve risk including potential loss of principal. Past performance 
                  is not indicative of future results. Please refer to the Private Placement Memorandum 
                  for complete terms and disclosures.
                </motion.p>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FamilyOfficesPage;
